namespace Solovis.Reports.Parts.Tables.DataTableHelpers.CellEditors {

  /**
   * Context passed to a CellEditorFactory so the editor can look up related row/column state.
   */
  export interface CellEditorContext {
    row: any;
    col: any;
    tableControl: Solovis.Reports.Parts.Tables.DataTableControl;
  }

  /**
   * A live editor instance for a single cell. The renderer owns its lifecycle.
   */
  export interface CellEditor {
    /** DOM element to attach inside the <td>. */
    element: HTMLElement;
    getValue(): any;
    setValue(value: any): void;
    /** Called when the cell is about to be removed (e.g. table rebuild). */
    destroy(): void;
  }

  /**
   * Factory signature used to construct an editor for a cell.
   * `onCommit` is invoked when the user finalizes a change (blur, Enter, etc.).
   */
  export type CellEditorFactory = (
    initialValue: any,
    ctx: CellEditorContext,
    onCommit: (newValue: any) => void
  ) => CellEditor;

  const _factories: { [type: string]: CellEditorFactory } = {};

  /**
   * Register a new editor type. Built-in editors are registered by the table itself;
   * report code can register additional editors before its first row renders.
   */
  export function registerEditor(type: string, factory: CellEditorFactory): void {
    _factories[type] = factory;
  }

  export function getEditor(type: string): CellEditorFactory {
    return _factories[type] ?? null;
  }

  export function hasEditor(type: string): boolean {
    return !!_factories[type];
  }

  /**
   * Default editor type for a DataColumnType (lowercased as it appears on the frontend column
   * definition, e.g. "number", "percentage", "bool"). Returns null if no built-in default
   * exists — callers must then supply an explicit `editor.type` on the column.
   */
  export function getDefaultEditorTypeForDataType(dataType: string): string | null {
    switch (dataType) {
      case "number": return "number";
      case "currency": return "currency";
      case "percentage": return "percentage";
      case "integer": return "integer";
      case "bool": return "boolean";
      case "string": return "text";
      default: return null;
    }
  }

  /**
   * Resolves which editor type to use for a column, honoring an explicit override
   * (`col.editor.type`) before falling back to the dataType default.
   */
  export function resolveEditorTypeForColumn(col: any): string | null {
    if (col?.editor?.type) {
      return col.editor.type;
    }
    return getDefaultEditorTypeForDataType(col?.dataType);
  }
}
